import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "components/shared/modal/Modal";
import styled from "styled-components/macro";
import useOnClickOutside from "helpers/useOnClickOutside";
import {
  COLORS,
  FONTSIZE,
  FONTWEIGHT,
  CONSTANTS
} from "helpers/designConstants";
import LabelledTextInput from "components/shared/LabelledTextInput";
import { Button } from "components/shared/standardElements";
import Checkbox from "components/shared/Checkbox";
import { useArticleReader } from "database/articles/useArticleReader";
import TemplateCataloguePicker from "./TemplateCataloguePicker";

import { useForm } from "react-hook-form";
import { useEffect } from "react";

const CreateEditCatalogueModal = ({
  title,
  show,
  onClose,
  onCreateUpdateCatalogue,
  catalogue,
  catalogues = [],
  ...rest
}) => {
  //HOOKS
  const { t } = useTranslation();
  const { activeSeasons } = useArticleReader();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm();
  const [useTemplate, setUseTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateMode, setTemplateMode] = useState("groups");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const templatePickerRef = useRef(null);
  useOnClickOutside(templatePickerRef, () => setShowTemplatePicker(false));

  useEffect(() => {
    setValue("catalogueName", catalogue?.name || "");
    if (catalogue) {
      setValue("seasonOptions", {
        seasonKey: catalogue.seasonKey || "",
        seasonName: catalogue.seasonName || "",
        seasonName_fr: catalogue.seasonName_fr || "",
        siloKey: catalogue.seasonSiloKey || "",
        siloName: catalogue.seasonSiloName || "",
        siloName_fr: catalogue.seasonSiloName_fr || ""
      });
    } else if (activeSeasons?.length > 0) {
      const latest = activeSeasons[activeSeasons.length - 1];
      setValue("seasonOptions", {
        seasonKey: latest.key,
        seasonName: latest.name,
        seasonName_fr: latest.name_fr,
        siloKey: "",
        siloName: "",
        siloName_fr: ""
      });
    }
    setValue(
      "priceToShowPreview",
      catalogue ? catalogue.priceToShowPreview : "MSRP"
    );
    setValue("sortOrder", catalogue ? catalogue.sortOrder : "collection");
    setValue("groupingMode", catalogue?.groupingMode || "schema");
  }, [catalogue, activeSeasons, setValue, show]);

  const watchPricePreviewSelection = watch("priceToShowPreview");
  const watchSortOrderSelection = watch("sortOrder");
  const watchGroupingMode = watch("groupingMode");

  const onSubmitForm = (data) => {
    if (useTemplate && selectedTemplate && data.groupingMode === "custom" && !catalogue) {
      data.templateCatalogueID = selectedTemplate.id;
      data.templateMode = templateMode;
    }
    onCreateUpdateCatalogue(data);
  };

  const onSelectClose = () => {
    onClose();
  };

  return (
    <Modal
      title={title}
      show={show}
      onClose={onSelectClose}
      disableCloseOnOutsideClick
      maxHeight={"90vh"}
      {...rest}
    >
      <Container>
        <StyledForm onSubmit={handleSubmit(onSubmitForm)}>
          <StyledLabelledTextInput
            shouldAutoFocus
            labelText={t("catalogue_name")}
            type='text'
            name='catalogueName'
            id='catalogueName'
            placeholder={t("catalogue_name")}
            {...register("catalogueName", { required: true })}
            error={errors?.bookingName}
          />
          {errors?.catalogueName?.type === "required" && (
            <StyledInputErrorMessage>
              {t("name_required") || "You must provide a name"}
            </StyledInputErrorMessage>
          )}
          <PricingWrapper>
            <PricingTitleWrapper>
              <PricingTitle>{t("show_price")}</PricingTitle>
            </PricingTitleWrapper>
            <PricingInnerContainer>
              <StyledCheckbox
                color={COLORS.text}
                label={t("MSRP")}
                selected={watchPricePreviewSelection === "MSRP"}
                type='radio'
                id='radio-preview-msrp'
                value='MSRP'
                {...register("priceToShowPreview")}
              />
              <StyledCheckbox
                color={COLORS.text}
                label={t("WHSL")}
                selected={watchPricePreviewSelection === "WHSL"}
                id='radio-preview-whsl'
                type='radio'
                value='WHSL'
                {...register("priceToShowPreview")}
              />
              <StyledCheckbox
                color={COLORS.text}
                label={`${t("MSRP")} + ${t("WHSL")}`}
                selected={watchPricePreviewSelection === "MSRP+WHSL"}
                id='radio-preview-msrp+whsl'
                type='radio'
                value='MSRP+WHSL'
                {...register("priceToShowPreview")}
              />
              <StyledCheckbox
                color={COLORS.text}
                label={t("none")}
                selected={watchPricePreviewSelection === "NONE"}
                id='radio-preview-none'
                type='radio'
                value='NONE'
                {...register("priceToShowPreview")}
              />
            </PricingInnerContainer>
          </PricingWrapper>
          <SortOrderWrapper>
            <SortOrderTitleWrapper>
              <SortOrderTitle>{t("sort")}</SortOrderTitle>
            </SortOrderTitleWrapper>
            <SortOrderInnerContainer>
              <StyledCheckbox
                color={COLORS.text}
                label={t("collection")}
                selected={watchSortOrderSelection === "collection"}
                id='radio-collection'
                type='radio'
                value='collection'
                {...register("sortOrder")}
              />
              <StyledCheckbox
                color={COLORS.text}
                label={t("category")}
                selected={watchSortOrderSelection === "category"}
                id='radio-category'
                type='radio'
                value='category'
                {...register("sortOrder")}
              />
            </SortOrderInnerContainer>
          </SortOrderWrapper>
          <GroupingModeWrapper>
            <GroupingModeTitleWrapper>
              <GroupingModeTitle>{t("grouping_mode") || "GROUPING"}</GroupingModeTitle>
            </GroupingModeTitleWrapper>
            <GroupingModeInnerContainer>
              <StyledCheckbox
                color={COLORS.text}
                label={t("auto_group_by_season") || "Auto-group by Season"}
                selected={watchGroupingMode === "schema"}
                id='radio-grouping-schema'
                type='radio'
                value='schema'
                {...register("groupingMode")}
              />
              <StyledCheckbox
                color={COLORS.text}
                label={t("custom_sections") || "Custom Sections"}
                selected={watchGroupingMode === "custom"}
                id='radio-grouping-custom'
                type='radio'
                value='custom'
                {...register("groupingMode")}
              />
            </GroupingModeInnerContainer>
          </GroupingModeWrapper>
          {watchGroupingMode === "custom" && !catalogue && (
            <TemplateWrapper>
              <TemplateToggle>
                <StyledCheckbox
                  color={COLORS.text}
                  label={t("copy_from_existing_catalogue") || "Copy from existing catalogue"}
                  selected={useTemplate}
                  type='checkbox'
                  id='template-toggle'
                  onChange={() => {
                    setUseTemplate((v) => !v);
                    if (useTemplate) {
                      setSelectedTemplate(null);
                      setShowTemplatePicker(false);
                    }
                  }}
                />
              </TemplateToggle>
              {useTemplate && (
                <TemplateBody>
                  <TemplatePickerAnchor ref={templatePickerRef}>
                    {selectedTemplate ? (
                      <SelectedBadge>
                        <span>{selectedTemplate.name}</span>
                        <DeselectButton
                          type='button'
                          onClick={() => {
                            setSelectedTemplate(null);
                            setShowTemplatePicker(false);
                          }}
                        >
                          ✕
                        </DeselectButton>
                      </SelectedBadge>
                    ) : (
                      <ChooseCatalogueButton
                        type='button'
                        onClick={() => setShowTemplatePicker((v) => !v)}
                      >
                        {t("choose_catalogue") || "Choose catalogue…"}
                      </ChooseCatalogueButton>
                    )}
                    {showTemplatePicker && !selectedTemplate && (
                      <TemplatePickerPopover>
                        <TemplateCataloguePicker
                          catalogues={catalogues}
                          onSelect={(cat) => {
                            setSelectedTemplate(cat);
                            setShowTemplatePicker(false);
                          }}
                          excludeID={catalogue?.id}
                        />
                      </TemplatePickerPopover>
                    )}
                  </TemplatePickerAnchor>
                  {selectedTemplate && (
                    <TemplateModeRow>
                      <StyledCheckbox
                        color={COLORS.text}
                        label={t("groups_only") || "Groups only"}
                        selected={templateMode === "groups"}
                        type='radio'
                        id='template-mode-groups'
                        value='groups'
                        onChange={() => setTemplateMode("groups")}
                      />
                      <StyledCheckbox
                        color={COLORS.text}
                        label={t("groups_and_articles") || "Groups + Articles"}
                        selected={templateMode === "groups_and_articles"}
                        type='radio'
                        id='template-mode-articles'
                        value='groups_and_articles'
                        onChange={() => setTemplateMode("groups_and_articles")}
                      />
                    </TemplateModeRow>
                  )}
                </TemplateBody>
              )}
            </TemplateWrapper>
          )}
          <Button style={{ marginTop: 10 }}>
            {catalogue == null ? t("create_button") : t("update_button")}
          </Button>
        </StyledForm>
      </Container>
    </Modal>
  );
};

const Container = styled.div`
  height: 100%;
  min-width: 350px;
  padding: 5px;
  /* max-height: 80vh; */
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
`;

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledLabelledTextInput = styled(LabelledTextInput)`
  input {
    border-color: ${({ error }) => error && COLORS.alertRed};
  }
`;

const StyledInputErrorMessage = styled.p`
  margin-top: 0px;
  padding: 5px;
  color: ${COLORS.alertRed};
  font-size: ${FONTSIZE.xsmall};
  align-self: flex-start;
`;

const PricingWrapper = styled.div`
  margin: 16px 0 10px;
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: stretch;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
`;

const PricingTitleWrapper = styled.div`
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

const PricingTitle = styled.div`
  background: ${COLORS.primaryInverted};
  padding: 0 5px;
  color: ${COLORS.text};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
`;

const PricingInnerContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  padding: 16px 20px;
`;

const InnerContainerTitle = styled.div`
  padding: 0 5px 10px;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.small};
  font-weight: ${FONTWEIGHT.heavy};
  text-decoration: underline;
`;

const Divider = styled.div`
  width: 1px;
  margin: 30px 0;
  background-color: ${COLORS.secondary};
`;

const StyledCheckbox = styled(Checkbox)`
  text-transform: uppercase;
  color: ${COLORS.text};
  padding: 5px 0;
`;

const SortOrderWrapper = styled.div`
  margin: 16px 0 10px;
  width: 100%;
  position: relative;
  display: flex;
  align-items: stretch;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
`;

const SortOrderTitleWrapper = styled.div`
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

const SortOrderTitle = styled.div`
  background: ${COLORS.primaryInverted};
  padding: 0 5px;
  color: ${COLORS.text};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
`;

const SortOrderInnerContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  padding: 16px 20px;
`;

const GroupingModeWrapper = styled.div`
  margin: 16px 0 10px;
  width: 100%;
  position: relative;
  display: flex;
  align-items: stretch;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
`;

const GroupingModeTitleWrapper = styled.div`
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

const GroupingModeTitle = styled.div`
  background: ${COLORS.primaryInverted};
  padding: 0 5px;
  color: ${COLORS.text};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
`;

const GroupingModeInnerContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 16px;
  padding: 16px 20px;
`;

const TemplateWrapper = styled.div`
  width: 100%;
  margin: 0 0 16px;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  padding: 12px 16px;
`;

const TemplateToggle = styled.div`
  cursor: pointer;
`;

const TemplateBody = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TemplatePickerAnchor = styled.div`
  position: relative;
`;

const ChooseCatalogueButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  background: ${COLORS.tertiary};
  color: ${COLORS.text};
  font-size: ${FONTSIZE.xsmall};
  text-transform: uppercase;
  font-weight: ${FONTWEIGHT.heavy};
  cursor: pointer;
  text-align: left;
  &:hover {
    border-color: ${COLORS.primary};
  }
`;

const TemplatePickerPopover = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin-bottom: 4px;
  background: white;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  padding: 10px;
`;

const SelectedBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background: ${COLORS.tertiary};
  border-radius: ${CONSTANTS.borderRadius};
  font-size: ${FONTSIZE.xsmall};
  text-transform: uppercase;
`;

const DeselectButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${FONTSIZE.small};
  color: ${COLORS.text};
  padding: 0 4px;
  &:hover {
    color: ${COLORS.primary};
  }
`;

const TemplateModeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

export default CreateEditCatalogueModal;
